'use client';

import { useState } from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './components/table';
import './data-table.scss';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'react-feather';

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    onAdd?: () => void;
    onEdit?: (row: TData) => void;
    onDelete?: (rows: TData[]) => void;
    showCheckbox?: boolean;
    showAdd?: boolean;
    loading?: boolean;
}

export function DataTable<TData, TValue>({
    columns,
    data,
    loading,
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});
    const [globalFilter, setGlobalFilter] = useState('');

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: setGlobalFilter,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            globalFilter
        },
    });

    return (
        <div className={'dataTable'}>
            <div className={'tableWrapper'}>
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isSortable = header.column.getCanSort();
                                    return (
                                        <TableHead key={header.id}
                                            onClick={isSortable ? () => header.column.toggleSorting() : undefined}
                                            className={"tableHead " + (isSortable ? "sortable" : 'static')}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {isSortable && (
                                                        <span className="sort-indicator">
                                                            {header.column.getIsSorted() === 'asc' ? <ChevronUp width={14} height={14} /> : header.column.getIsSorted() === 'desc' ? <ChevronDown width={14} height={14} /> : ''}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                {loading ? (
                                    <TableCell
                                        colSpan={columns.length + 1}
                                        className={'noResults'}
                                    >
                                        <div className="wrapper">
                                            Daten werden abgerufen..
                                        </div>
                                    </TableCell>
                                ) : (
                                    <TableCell
                                        colSpan={columns.length + 1}
                                        className={'noResults'}
                                    >
                                        <div className="wrapper">
                                            Keine Einträge gefunden..
                                        </div>
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className={'tablePagination'}>
                <div className={'paginationInfo'}>
                    
                </div>
                <div className={'paginationControls'}>
                    <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <ChevronLeft className={'buttonIcon'} />
                        Vorherige
                    </button>
                    <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Nächste
                        <ChevronRight className={'buttonIcon'} />
                    </button>
                </div>
            </div>
        </div>
    );
}
